import React, { useState, useEffect, useCallback, useMemo } from "react";
import "../styles.css";
import { useDispatch, useSelector } from "react-redux";
import { fetchUsers } from "../../../redux/slices/userSlice";
import { fetchHolidays } from "../../../redux/slices/absenceSlice";
import {
  fetchProjects,
  fetchProjectMembers,
} from "../../../redux/slices/projectsSlice";
import { toast } from "react-toastify";
import {
  Container,
  Modal,
  Button,
  Form,
  Row,
  Col,
  Badge,
} from "react-bootstrap";

import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import { ChromePicker } from "react-color";
import "rsuite/dist/rsuite.min.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-datepicker/dist/react-datepicker.css";
import DatePicker from "react-datepicker";
import { TimePicker } from "rsuite";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import format from "date-fns/format";
import parse from "date-fns/parse";
import startOfWeek from "date-fns/startOfWeek";
import getDay from "date-fns/getDay";
import pt from "date-fns/locale/pt";
import enUS from "date-fns/locale/en-US";
import { useTranslation } from "react-i18next";
import AutocompleteSelect from "../../autocomplete/AutocompleteSelect/AutocompleteSelect";
import CalendarNavigation from "../CalendarNavigation/CalendarNavigation";
import {
  fetchEvents,
  createEvent,
  updateEvent,
  deleteEvent,
  setProjectFilter,
} from "../../../redux/slices/calendarSlice";
import { fetchAbsences } from "../../../redux/slices/absenceSlice";
import { normalizeLanguageCode } from "../../../utils/language";
import { formatDateTime, formatTime } from "../../../utils/timeFormat";

const locales = { pt: pt, en: enUS };
const EVENT_CATEGORIES = ["Project Meeting", "Technical Meeting", "Team building", "Other"];
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales,
});
const DnDCalendar = withDragAndDrop(Calendar);

const getInitialDates = () => {
  const now = new Date();
  const end = new Date(now.getTime() + 60 * 60 * 1000);
  return { now, end };
};

const mergeDateAndTime = (datePart, timePart) => {
  const result = new Date(datePart);
  if (timePart instanceof Date && !isNaN(timePart)) {
    result.setHours(timePart.getHours());
    result.setMinutes(timePart.getMinutes());
    result.setSeconds(0);
    result.setMilliseconds(0);
  }
  return result;
};

const EventComponent = ({ event }) => {
  const startTime = event.allDay ? "" : formatTime(event.start);
  const endTime = event.allDay ? "" : formatTime(event.end);
  const projects = useSelector((state) => state.projects?.items || []);
  const _project = projects.find((p) => p.id === event.project);

  return (
    <div
      className="custom-event-card"
      style={{
        display: "flex",
        flexDirection: "column",
        padding: "2px 4px",
        overflow: "hidden",
      }}
    >
      <div
        className="event-title"
        style={{
          fontWeight: "bold",
          fontSize: "0.85rem",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {event.title}
      </div>

      <div className="event-details">
        {!event.allDay && (
          <div
            className="event-time"
            style={{ fontSize: "0.75rem", opacity: 0.9, marginBottom: "4px" }}
          >
            {startTime} - {endTime}
          </div>
        )}

        {event.description && (
          <div
            className="event-desc"
            style={{
              fontSize: "0.75rem",
              opacity: 0.85,
              marginBottom: "8px",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {event.description}
          </div>
        )}

        <div style={{ marginTop: "auto", paddingTop: "4px" }}>
          {event.attendees && event.attendees.length > 0 && (
            <div className="avatar-stack d-flex align-items-center">
              {event.attendees.slice(0, 3).map((p, idx) => (
                <div
                  key={p.id}
                  className="avatar"
                  style={{
                    zIndex: 10 - idx,
                    width: "25px",
                    height: "25px",
                    marginLeft: idx === 0 ? "0" : "-8px",
                  }}
                  title={p.name}
                >
                  <img
                    src={p.avatar}
                    alt={p.name}
                    style={{
                      width: "100%",
                      height: "100%",
                      borderRadius: "50%",
                      border: "0px solid rgba(255, 255, 255, 0.4)",
                    }}
                  />
                </div>
              ))}
              {event.attendees.length > 3 && (
                <span
                  style={{
                    fontSize: "0.65rem",
                    marginLeft: "4px",
                    fontWeight: "bold",
                  }}
                >
                  +{event.attendees.length - 3}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const CustomDateHeader = ({ label, date, holidayMap }) => {
  const dateStr = format(date, "yyyy-MM-dd");
  const holidayName = holidayMap ? holidayMap.get(dateStr) : null;

  if (holidayName) {
    return (
      <div
        title={holidayName}
        style={{
          display: "flex",
          justifyContent: "flex-end",
          width: "100%",
          padding: "2px 5px",
        }}
      >
        <span
          style={{
            backgroundColor: "#d44d5b2c",
            color: "red",
            borderRadius: "50%",
            width: "25px",
            height: "25px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: "bold",
            fontSize: "0.85rem",
            cursor: "auto",
            boxShadow: "0 2px 5px rgba(220, 53, 69, 0.4)",
          }}
        >
          {label}
        </span>
      </div>
    );
  }

  return <span style={{ paddingRight: "5px" }}>{label}</span>;
};

const CalendarComponent = ({ variant = "full" }) => {
  const isCompact = variant === "compact";
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [holidays, setHolidays] = useState([]);
  
  const holidayMap = useMemo(() => {
    return new Map(
      holidays.map((h) => {
        const cleanDate = h.date ? h.date.split("T")[0] : "";
        return [cleanDate, h.label || h.name || t("components.Calendar.holiday_fallback")];
      }),
    );
  }, [holidays, t]);
  
  const user = useSelector((state) => state.auth.user);

  useEffect(() => {
    if (user) {
      dispatch(fetchEvents());
      dispatch(fetchProjects());
      dispatch(fetchUsers());
      dispatch(fetchAbsences());
      dispatch(fetchHolidays({ year: new Date().getFullYear() }))
        .unwrap()
        .then((data) => setHolidays(data))
        .catch((err) => console.error("Erro ao buscar feriados:", err));
    }
  }, [dispatch, user]);

  const { events, selectedProject, isLoading } = useSelector(
    (state) => state.calendar,
  );

  const [showCoworkers, setShowCoworkers] = useState(true);
  const { items: absences = [] } = useSelector((state) => state.absences);

  const myAbsenceEvents = (absences || [])
    .filter((abs) => abs.status === "Accepted" && abs.user_id === user?.id)
    .map((abs) => {
      const endDate = new Date(abs.end_date);
      if (!abs.is_half_day) endDate.setDate(endDate.getDate() + 1);

      return {
        id: `absence-${abs.id}`,
        title: abs.absence_type,
        start: new Date(abs.start_date),
        end: endDate,
        allDay: !abs.is_half_day,
        type: "absence",
        color: "#2163ce",
      };
    });

  const coworkerAbsenceEvents = (absences || [])
    .filter((abs) => abs.status === "Accepted" && abs.user_id !== user?.id)
    .map((abs) => {
      const endDate = new Date(abs.end_date);
      if (!abs.is_half_day) endDate.setDate(endDate.getDate() + 1);

      const fullName =
        abs.first_name && abs.last_name
          ? `${abs.first_name} ${abs.last_name}`
          : abs.user_name || t("components.Calendar.unknown");

      return {
        id: `absence-${abs.id}`,
        title: `${fullName} (${abs.absence_type})`,
        start: new Date(abs.start_date),
        end: endDate,
        allDay: !abs.is_half_day,
        type: "coworker_absence",
        color: "rgba(14, 70, 163, 0.4)",
      };
    });

  const { items: projects = [] } = useSelector((state) => state.projects || {});
  const { items: dbUsers = [] } = useSelector((state) => state.users || {});

  const [currentView, setCurrentView] = useState("month");
  const [currentDate, setCurrentDate] = useState(new Date());
  const { now, end } = getInitialDates();

  const [newEvent, setNewEvent] = useState({
    id: null,
    title: "",
    description: "",
    type: "Project Meeting",
    project: "None",
    start: now,
    end: end,
    color: "#0205b4",
    allDay: false,
    attendees: [],
  });

  const [validated, setValidated] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  const calendarEvents = events.map((event) => ({
    ...event,
    start: new Date(event.start),
    end: new Date(event.end),
  }));

  const filteredCalendarEvents =
    selectedProject === "All"
      ? calendarEvents
      : calendarEvents.filter((e) => e.project === selectedProject);

  const displayedEvents = [
    ...filteredCalendarEvents, 
    ...myAbsenceEvents, 
    ...(showCoworkers ? coworkerAbsenceEvents : [])
  ];

  const handleAttendeeToggle = (user) => {
    setNewEvent((prev) => {
      const isSelected = prev.attendees?.some((a) => a.id === user.id);
      if (isSelected)
        return {
          ...prev,
          attendees: prev.attendees.filter((a) => a.id !== user.id),
        };
      return { ...prev, attendees: [...(prev.attendees || []), user] };
    });
  };

  const handleDeleteEvent = async () => {
    if (!selectedEvent) return;
    try {
      await dispatch(deleteEvent(selectedEvent.id)).unwrap();
      setShowEventModal(false);
      setIsConfirmingDelete(false);
    } catch (error) {
      toast.error(t("components.Calendar.delete_error"));
    }
  };

  const handleSaveEvent = async () => {
    if (!newEvent.title.trim()) {
      toast.warning(t("components.Calendar.title_required"));
      setValidated(true);
      return;
    }

    let dataInicio = new Date(newEvent.start);
    let dataFim = new Date(newEvent.end);

    if (newEvent.allDay) {
      dataInicio.setHours(0, 0, 0, 0);
      dataFim.setHours(23, 59, 59, 999);
    } else if (dataFim <= dataInicio) {
      dataFim = new Date(dataInicio.getTime() + 60 * 60 * 1000);
    }

    const eventPayload = {
      event_name: newEvent.title,
      event_description: newEvent.description || " ",
      start_time: format(dataInicio, "yyyy-MM-dd'T'HH:mm:ss"),
      end_time: format(dataFim, "yyyy-MM-dd'T'HH:mm:ss"),
      all_day: newEvent.allDay,
      category: newEvent.type,
      invitees: newEvent.attendees,
      color: newEvent.color,
      project_id: newEvent.project === "None" ? null : newEvent.project,
    };

    try {
      const isEditing = newEvent.id !== null;
      if (isEditing) {
        await dispatch(
          updateEvent({ id: newEvent.id, payload: eventPayload }),
        ).unwrap();
      } else {
        await dispatch(createEvent(eventPayload)).unwrap();
      }

      setShowAddModal(false);
      setValidated(false);

      const { now, end } = getInitialDates();
      setNewEvent({
        id: null,
        title: "",
        description: "",
        type: "Project Meeting",
        project: "None",
        start: now,
        end: end,
        color: "#0205b4",
        allDay: false,
        attendees: [],
      });
    } catch (error) {
      toast.error(t("components.Calendar.save_error"));
    }
  };

  const handleEditClick = () => {
    if (
      selectedEvent.type === "absence" ||
      selectedEvent.type === "coworker_absence"
    ) {
      return;
    }
    setNewEvent({ ...selectedEvent });
    setValidated(false);
    setShowEventModal(false);
    setShowAddModal(true);
  };

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    setIsConfirmingDelete(false);
    setShowEventModal(true);
  };

  const handleProjectSelect = async (e) => {
    const selectedId = e.target.value;

    if (selectedId === "None" || selectedId === "All") {
      setNewEvent((prev) => ({
        ...prev,
        project: "None",
        color: "#718ddd",
        attendees: [],
      }));
      return;
    }

    const projectData = projects.find(
      (p) => String(p.id) === String(selectedId),
    );

    if (projectData) {
      setNewEvent((prev) => ({
        ...prev,
        project: selectedId,
        color: projectData.color,
      }));

      try {
        const response = await dispatch(
          fetchProjectMembers(selectedId),
        ).unwrap();
        const projectMembersList = response.members || [];
        const memberIds = projectMembersList.map((m) => String(m.user_id));

        setNewEvent((prev) => {
          const autoAttendees = dbUsers
            .filter((user) => memberIds.includes(String(user.id)))
            .map((user) => ({
              id: user.id,
              value: user.id,
              label: `${user.first_name} ${user.last_name}`,
              name: `${user.first_name} ${user.last_name}`,
              avatar:
                user.profile_picture ||
                `https://ui-avatars.com/api/?name=${user.first_name}+${user.last_name}&background=random&color=fff`,
            }));

          return {
            ...prev,
            attendees: autoAttendees,
          };
        });
      } catch (error) {
        toast.error(t("components.Calendar.members_unauthorized"));
      }
    }
  };

  const handleEventMove = async ({ event, start, end, isAllday }) => {
    const isAbsence = event.type?.includes("absence");

    if (isAbsence) {
      return;
    }

    const eventAllday = isAllday !== undefined ? isAllday : event.allDay;
    const updatePayload = {
      event_name: event.title,
      event_description: event.description || " ",
      start_time: format(start, "yyyy-MM-dd'T'HH:mm:ss"),
      end_time: format(end, "yyyy-MM-dd'T'HH:mm:ss"),
      category: event.type,
      invitees: event.attendees,
      project_id: null,
      all_day: eventAllday,
    };
    try {
      await dispatch(
        updateEvent({ id: event.id, payload: updatePayload }),
      ).unwrap();
    } catch (error) {
      toast.error(t("components.Calendar.members_load_error"));
      dispatch(fetchEvents());
    }
  };

  const handleStartDateChange = (date) => {
    if (!date) return;
    setNewEvent((prev) => ({
      ...prev,
      start: mergeDateAndTime(date, prev.start),
    }));
  };

  const handleEndDateChange = (date) => {
    if (!date) return;
    setNewEvent((prev) => ({ ...prev, end: mergeDateAndTime(date, prev.end) }));
  };

  const handleStartTimeChange = (time) => {
    if (!time) return;
    setNewEvent((prev) => ({
      ...prev,
      start: mergeDateAndTime(prev.start, time),
    }));
  };

  const handleEndTimeChange = (time) => {
    if (!time) return;
    setNewEvent((prev) => ({ ...prev, end: mergeDateAndTime(prev.end, time) }));
  };

  const disableStartHour = useCallback(
    (hour) => {
      const now = new Date();
      return (
        newEvent.start.toDateString() === now.toDateString() &&
        hour < now.getHours()
      );
    },
    [newEvent.start],
  );

  const disableStartMinute = useCallback(
    (minute, date) => {
      const now = new Date();
      const isToday = newEvent.start.toDateString() === now.toDateString();
      const hourToCheck = date ? date.getHours() : newEvent.start.getHours();

      return (
        isToday && hourToCheck === now.getHours() && minute < now.getMinutes()
      );
    },
    [newEvent.start],
  );

  const disableEndHour = useCallback(
    (hour) => {
      return (
        newEvent.start.toDateString() === newEvent.end.toDateString() &&
        hour < newEvent.start.getHours()
      );
    },
    [newEvent.start, newEvent.end],
  );

  const disableEndMinute = useCallback(
    (minute, date) => {
      const isSameDay =
        newEvent.start.toDateString() === newEvent.end.toDateString();
      const hourToCheck = date ? date.getHours() : newEvent.end.getHours();
      const startHour = newEvent.start.getHours();

      return (
        isSameDay &&
        hourToCheck === startHour &&
        minute <= newEvent.start.getMinutes()
      );
    },
    [newEvent.start, newEvent.end],
  );

  const eventStyleGetter = (event) => {
    const isCoworkerAbsence = event.type === "coworker_absence";
    return {
      style: {
        backgroundColor: event.color || "#b2bbda",
        borderRadius: "6px",
        color: isCoworkerAbsence
          ? "rgba(255,255,255,0.8)"
          : event.type === "holiday"
            ? "black"
            : "white",
        border: "none",
        padding: "2px",
        opacity: isCoworkerAbsence ? 0.8 : 1,
      },
    };
  };

  const toggleLanguage = () => {
    const newLang = i18n.language.startsWith("pt") ? "en" : "pt";
    i18n.changeLanguage(newLang);
  };

  const CustomToolbar = (toolbar) => {
    return (
      <div className="d-flex justify-content-between align-items-center mb-3 gap-2" style={{ width: "100%" }}>
        <CalendarNavigation
          label={toolbar.label}
          onPrevious={() => toolbar.onNavigate("PREV")}
          onNext={() => toolbar.onNavigate("NEXT")}
          onToday={() => toolbar.onNavigate("TODAY")}
        />

        <div className="d-flex gap-1 bg-surface p-1 rounded-3 border" style={{ flexShrink: 0 }}>
          {["agenda", "day", "week", "month"].map((viewName) => (
            <Button
              key={viewName}
              variant="transparent"
              size="sm"
              onClick={() => toolbar.onView(viewName)}
              className="fw-bold border-0 shadow-none m-0"
              style={{
                backgroundColor: toolbar.view === viewName ? "var(--bg-surface)" : "transparent",
                color: toolbar.view === viewName ? "var(--brand-primary)" : "var(--text-secondary)",
                boxShadow: toolbar.view === viewName ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                borderRadius: "4px",
                textTransform: "capitalize",
                padding: "2px 8px",
                fontSize: "0.75rem"
              }}
            >
              {t(`components.Calendar.views.${viewName}`)}
            </Button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className={`calendar-root-scope ${variant}-variant`}>
      <Container
        fluid
        className={`calendar-main-container d-flex flex-column ${isCompact ? "h-auto p-0" : "h-100 pt-4 px-4"}`}
      >
        {!isCompact && (
          <div className="calendar-header-actions mb-4">
            <div className="d-flex gap-3 align-items-center flex-wrap">
              <Form.Select
                size="sm"
                className="bg-surface txt-main border shadow-none"
                style={{
                  width: "200px",
                  height: "40px",
                  cursor: "pointer",
                  fontWeight: "500",
                }}
                value={selectedProject}
                onChange={(e) => dispatch(setProjectFilter(e.target.value))}
              >
                <option value="All">
                  {t("components.Calendar.all_projects")}
                </option>
                {projects.map((proj) => (
                  <option key={proj.id} value={proj.id}>
                    {proj.name}
                  </option>
                ))}
              </Form.Select>

              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowAddModal(true)}
                style={{
                  backgroundColor: "var(--brand-hover)",
                  border: "none",
                  height: "40px",
                  fontWeight: "600",
                  borderRadius: "var(--border-radius)",
                  fontSize: "0.85rem",
                  padding: "0 1.5rem",
                }}
              >
                + {t("components.Calendar.add_event")}
              </Button>

              <Form.Check
                type="switch"
                id="coworkers-switch"
                label={t("components.Calendar.team_absences")}
                checked={showCoworkers}
                onChange={(e) => setShowCoworkers(e.target.checked)}
                className="pt-1 txt-main fw-bold"
                style={{ cursor: "pointer" }}
              />

              <Button
                variant="outline-secondary"
                size="sm"
                onClick={toggleLanguage}
                className="txt-main fw-bold"
                style={{
                  height: "40px",
                  borderRadius: "var(--border-radius)",
                  padding: "0 1rem",
                }}
              >
                {i18n.language?.startsWith("pt") ? "EN" : "PT"}
              </Button>
            </div>
          </div>
        )}

        <div
          className={`atlar-card flex-grow-1 d-flex flex-column ${isCompact ? "p-2 border-0 shadow-none m-0" : "mb-4"}`}
        >
          <div
            className="calendar-harness flex-grow-1"
            style={{ minHeight: isCompact ? "500px" : "0", height: isCompact ? "500px" : "100%" }}
          >
            <DnDCalendar
              style={{ height: "100%" }}
              localizer={localizer}
              events={displayedEvents}
              date={currentDate}
              onNavigate={setCurrentDate}
              view={currentView}
              onView={setCurrentView}
              resizable
              selectable
              onEventDrop={handleEventMove}
              onEventResize={handleEventMove}
              culture={normalizeLanguageCode(i18n.language)}
              popup={true}
              messages={{
                month: t("components.Calendar.views.month"),
                week: t("components.Calendar.views.week"),
                day: t("components.Calendar.views.day"),
                agenda: t("components.Calendar.views.agenda"),
                today: t("components.Calendar.today"),
                previous: t("components.Calendar.previous"),
                next: t("components.Calendar.next"),
              }}
              views={["month", "week", "day", "agenda"]}
              onSelectEvent={handleSelectEvent}
              eventPropGetter={eventStyleGetter}
              components={{
                toolbar: CustomToolbar, 
                event: EventComponent,
                month: {
                  dateHeader: (props) => (
                    <CustomDateHeader
                      {...props}
                      holidayMap={holidayMap}
                    />
                  ),
                },
              }}
            />
          </div>
        </div>
      </Container>

      <Modal
        show={showEventModal}
        onHide={() => setShowEventModal(false)}
        centered
      >
        <Modal.Header
          closeButton
          className="bg-surface border-0"
          style={{ padding: "1.5rem 1.5rem 0.5rem" }}
        >
          <Modal.Title className="txt-main fw-bold d-flex align-items-center gap-2">
            <div
              style={{
                width: "12px",
                height: "12px",
                borderRadius: "50%",
                backgroundColor: selectedEvent?.color || "var(--brand-primary)",
                flexShrink: 0,
              }}
            />
            {selectedEvent?.title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 bg-surface">
          <div className="d-flex gap-2 mb-4">
            {selectedEvent?.type !== "absence" &&
              selectedEvent?.type !== "coworker_absence" && (
                <Badge
                  style={{
                    backgroundColor:
                      projects.find(
                        (p) => String(p.id) === String(selectedEvent?.project),
                      )?.color || "var(--gray-400)",
                    border: "none",
                    padding: "5px 10px",
                    borderRadius: "6px",
                  }}
                >
                  {projects.find(
                    (p) => String(p.id) === String(selectedEvent?.project),
                  )?.name || t("components.Calendar.no_project")}
                </Badge>
              )}

            <Badge
              className="text-capitalize"
              style={{
                backgroundColor: "var(--bg-surface)",
                color: "var(--brand-primary)",
                border: "1px solid var(--brand-primary)",
                padding: "5px 10px",
                borderRadius: "6px",
              }}
            >
              {t(`components.Calendar.categories.${selectedEvent?.type}`, { defaultValue: selectedEvent?.type })}
            </Badge>
          </div>
          <p className="mb-1">
            <strong>{t("components.Calendar.start")}:</strong>{" "}
            {formatDateTime(selectedEvent?.start, i18n.language)}
          </p>
          <p className="mb-3">
            <strong>{t("components.Calendar.end")}:</strong>{" "}
            {formatDateTime(selectedEvent?.end, i18n.language)}
          </p>
          {selectedEvent?.description && (
            <div
              className="mb-3 p-3 bg-surface rounded txt-subtle"
              style={{ fontSize: "0.9rem" }}
            >
              {selectedEvent.description}
            </div>
          )}
          {selectedEvent?.attendees && selectedEvent.attendees.length > 0 && (
            <div className="mt-4 pt-3 border-top">
              <h6 className="mb-2 text-muted participants">
                {t("components.Calendar.attendees")} (
                {selectedEvent.attendees.length})
              </h6>
              <div className="d-flex align-items-center gap-2">
                <div className="avatar-stack m-0">
                  {selectedEvent.attendees.slice(0, 2).map((p, idx) => (
                    <div
                      key={p.id}
                      className="avatar"
                      style={{
                        zIndex: 10 - idx,
                        width: "32px",
                        height: "32px",
                        marginLeft: idx === 0 ? "0" : "-10px",
                      }}
                      title={p.name}
                    >
                      {p.avatar ? (
                        <img src={p.avatar} alt={p.name} />
                      ) : (
                        p.name[0]
                      )}
                    </div>
                  ))}
                  {selectedEvent.attendees.length > 2 && (
                    <div
                      className="avatar more"
                      style={{
                        zIndex: 0,
                        width: "32px",
                        height: "32px",
                        marginLeft: "-10px",
                      }}
                    >
                      +{selectedEvent.attendees.length - 2}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer
          className={`border-0 bg-surface pb-4 px-4 ${
            isConfirmingDelete
              ? "justify-content-center"
              : "justify-content-end"
          }`}
        >
          {selectedEvent?.type !== "absence" &&
            selectedEvent?.type !== "coworker_absence" &&
            (!isConfirmingDelete ? (
              <div className="d-flex gap-3 w-100">
                <Button
                  variant="light"
                  className="flex-fill fw-bold"
                  style={{
                    padding: "10px 0",
                    borderRadius: "12px",
                    color: "var(--gray-600)",
                    backgroundColor: "rgba(239, 68, 68, 0.05)",
                    border: "none",
                    fontSize: "10px",
                  }}
                  onClick={() => setIsConfirmingDelete(true)}
                >
                  {t("components.Calendar.delete")}
                </Button>

                <Button
                  className="flex-fill fw-bold shadow-sm"
                  style={{
                    padding: "10px 0",
                    borderRadius: "12px",
                    backgroundColor: "var(--brand-primary)",
                    border: "none",
                    color: "white",
                    fontSize: "10px",
                  }}
                  onClick={handleEditClick}
                >
                  {t("components.Calendar.edit")}
                </Button>
              </div>
            ) : (
              <div className="d-flex flex-column w-100 gap-3 text-center">
                <span className="txt-main fw-bold fs-5 mb-1">
                  {t("components.Calendar.are_you_sure")}
                </span>

                <div className="d-flex gap-3">
                  <Button
                    variant="light"
                    className="flex-fill fw-bold"
                    style={{
                      padding: "10px 0",
                      borderRadius: "12px",
                      backgroundColor: "var(--gray-100)",
                      color: "var(--text-primary)",
                      border: "none",
                      fontSize: "15px",
                    }}
                    onClick={() => setIsConfirmingDelete(false)}
                  >
                    {t("components.Calendar.cancel")}
                  </Button>

                  <Button
                    variant="danger"
                    className="flex-fill fw-bold shadow-sm"
                    disabled={isLoading}
                    style={{
                      padding: "5px 0",
                      borderRadius: "12px",
                      backgroundColor: "var(--danger-red)",
                      border: "none",
                      fontSize: "15px",
                    }}
                    onClick={handleDeleteEvent}
                  >
                    {isLoading ? "..." : t("components.Calendar.confirm_delete")}
                  </Button>
                </div>
              </div>
            ))}
        </Modal.Footer>
      </Modal>

      <Modal
        show={showAddModal}
        onHide={() => {
          setShowAddModal(false);
          setValidated(false);
        }}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {newEvent.id
              ? t("components.Calendar.edit_event")
              : t("components.Calendar.new_event")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>
                {t("components.Calendar.title")} <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder={t("components.Calendar.title_placeholder")}
                value={newEvent.title}
                onChange={(e) => {
                  setNewEvent({ ...newEvent, title: e.target.value });
                  if (e.target.value.trim()) setValidated(false);
                }}
                isInvalid={validated && !newEvent.title.trim()}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                {t("components.Calendar.description")}
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={newEvent.description}
                onChange={(e) =>
                  setNewEvent({ ...newEvent, description: e.target.value })
                }
              />
            </Form.Group>

            <Row className="mb-3">
              <Col>
                <Form.Label>{t("components.Calendar.category")}</Form.Label>
                <Form.Select
                  value={newEvent.type}
                  onChange={(e) =>
                    setNewEvent({ ...newEvent, type: e.target.value })
                  }
                >
                  {EVENT_CATEGORIES.map((category) => (
                    <option key={category} value={category}>
                      {t(`components.Calendar.categories.${category}`)}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col>
                <Form.Label>{t("components.Calendar.project")}</Form.Label>
                <Form.Select
                  value={newEvent.project}
                  onChange={handleProjectSelect}
                >
                  <option value="All">
                    {t("components.Calendar.all_projects")}
                  </option>
                  {projects.map((proj) => (
                    <option key={proj.id} value={proj.id}>
                      {proj.name}
                  </option>
                ))}
                </Form.Select>
              </Col>
            </Row>

            <Row className="mb-3 align-items-center">
              <Col md={6} className="d-flex align-items-center gap-2">
                <Form.Label className="mb-0">
                  {t("components.Calendar.event_color")}
                </Form.Label>

                <div style={{ position: "relative" }}>
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "8px",
                      backgroundColor: newEvent.color,
                      cursor: "pointer",
                      border: "2px solid #dee2e6",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    }}
                    onClick={() => setShowColorPicker(!showColorPicker)}
                  />

                  {showColorPicker && (
                    <div
                      style={{
                        position: "absolute",
                        zIndex: 2000,
                        top: "45px",
                        left: "0",
                      }}
                    >
                      <div
                        style={{
                          position: "fixed",
                          top: 0,
                          right: 0,
                          bottom: 0,
                          left: 0,
                        }}
                        onClick={() => setShowColorPicker(false)}
                      />
                      <ChromePicker
                        color={newEvent.color}
                        onChangeComplete={(color) =>
                          setNewEvent({ ...newEvent, color: color.hex })
                        }
                        disableAlpha={true}
                      />
                    </div>
                  )}
                </div>
              </Col>
              <Col md={6}>
                <Form.Check
                  type="switch"
                  id="all-day-switch"
                  label={t("components.Calendar.all_day_event")}
                  checked={newEvent.allDay}
                  onChange={(e) =>
                    setNewEvent({ ...newEvent, allDay: e.target.checked })
                  }
                />
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={newEvent.allDay ? 12 : 6}>
                <Form.Label>{t("components.Calendar.start")}</Form.Label>
                <div style={{ position: "relative", zIndex: 1060 }}>
                  <DatePicker
                    selected={newEvent.start}
                    onChange={handleStartDateChange}
                    dateFormat="dd/MM/yyyy"
                    className="form-control"
                    minDate={new Date(new Date().setHours(0, 0, 0, 0))}
                    popperPlacement="bottom-start"
                    popperProps={{ strategy: "fixed" }}
                  />
                </div>
              </Col>
              {!newEvent.allDay && (
                <Col md={6}>
                  <Form.Label>{t("components.Calendar.time")}</Form.Label>
                  <TimePicker
                    format="HH:mm"
                    value={newEvent.start}
                    onChange={handleStartTimeChange}
                    cleanable={false}
                    style={{ width: "100%" }}
                    placement="auto"
                    shouldDisableHour={disableStartHour}
                    shouldDisableMinute={disableStartMinute}
                  />
                </Col>
              )}
            </Row>

            <Row className="mb-3">
              <Col md={newEvent.allDay ? 12 : 6}>
                <Form.Label>{t("components.Calendar.end")}</Form.Label>
                <div style={{ position: "relative", zIndex: 1050 }}>
                  <DatePicker
                    selected={newEvent.end}
                    onChange={handleEndDateChange}
                    dateFormat="dd/MM/yyyy"
                    className="form-control"
                    minDate={newEvent.start}
                    popperPlacement="bottom-start"
                    popperProps={{ strategy: "fixed" }}
                  />
                </div>
              </Col>
              {!newEvent.allDay && (
                <Col md={6}>
                  <Form.Label>{t("components.Calendar.time")}</Form.Label>
                  <TimePicker
                    format="HH:mm"
                    value={newEvent.end}
                    onChange={handleEndTimeChange}
                    cleanable={false}
                    style={{ width: "100%" }}
                    placement="auto"
                    shouldDisableHour={disableEndHour}
                    shouldDisableMinute={disableEndMinute}
                  />
                </Col>
              )}
            </Row>

            <Form.Group className="mb-4">
              <Form.Label>{t("components.Calendar.attendees")}</Form.Label>
              <div
                style={{
                  position: "relative",
                  zIndex: 100,
                  marginBottom: "10px",
                }}
              >
                <AutocompleteSelect
                  type="users"
                  data={dbUsers.filter(
                    (user) =>
                      !newEvent.attendees?.some((a) => a.id === user.id),
                  )}
                  value={null}
                  onSelect={(selectedOption) => {
                    if (!selectedOption) return;

                    const formattedUser = {
                      ...selectedOption,
                      id: selectedOption.value || selectedOption.id,
                      name: selectedOption.label || selectedOption.name,
                      avatar:
                        selectedOption.avatar ||
                        selectedOption.profile_picture ||
                        `https://ui-avatars.com/api/?name=${selectedOption.label}&background=random&color=fff`,
                    };

                    setNewEvent((prev) => {
                      const currentAttendees = prev.attendees || [];
                      const isAlreadyAdded = currentAttendees.some(
                        (a) => String(a.id) === String(formattedUser.id),
                      );

                      if (isAlreadyAdded) return prev;

                      return {
                        ...prev,
                        attendees: [...currentAttendees, formattedUser],
                      };
                    });
                  }}
                  placeholder={t("components.Calendar.search_users")}
                />
              </div>

              <div
                className="d-flex flex-wrap gap-2 p-2 border rounded bg-surface"
                style={{ minHeight: "50px" }}
              >
                {newEvent.attendees && newEvent.attendees.length > 0 ? (
                  newEvent.attendees.map((user) => (
                    <Badge
                      key={user.id}
                      bg="primary"
                      text="var(--gray-900)"
                      className="shadow-sm txt-main"
                      style={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        padding: "6px 12px",
                      }}
                      onClick={() => handleAttendeeToggle(user)}
                      title={t("components.Calendar.click_to_remove")}
                    >
                      <img
                        src={user.avatar}
                        alt={user.name}
                        style={{
                          width: "20px",
                          height: "20px",
                          borderRadius: "50%",
                          objectFit: "cover",
                        }}
                      />
                      {user.name}
                      <span style={{ marginLeft: "6px", fontWeight: "bold" }}>
                        &times;
                      </span>
                    </Badge>
                  ))
                ) : (
                  <span
                    className="text-muted w-100 text-center"
                    style={{ fontSize: "0.85rem", alignSelf: "center" }}
                  >
                    {t("components.Calendar.no_attendees")}
                  </span>
                )}
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            className="cancelButton"
            disabled={isLoading}
            onClick={() => {
              setShowAddModal(false);
              setValidated(false);
            }}
          >
            {t("components.Calendar.cancel")}
          </Button>
          <Button
            variant="primary"
            className="saveButton"
            disabled={isLoading}
            onClick={handleSaveEvent}
          >
            {isLoading
              ? t("components.Calendar.saving")
              : t("components.Calendar.save")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CalendarComponent;
